/**
 * 承認案件一覧画面 (/admin/approval-tasks)
 *
 * 本体 (フィルタ + テーブル + 承認/差戻しダイアログ) は `ApprovalTaskList` に切り出し、
 * 本ページは layout / breadcrumb / ページ説明のラッパーに徹する。
 *
 * スポット入札の承認/差戻しは本画面ではなくスポット入札画面の承認状態バナーで行う
 * (spec `spot-bidding.md` AC-BSP-094、 PR #6519。 本画面は release-scope.md で P2-1 =
 * 現時点で動作保証対象外)。 そのため APPROVE / REJECT の遷移実装が
 * `ApprovalTaskList` と `SpotBidding/hooks/useSpotApprovalDecision` の 2 箇所に並存する。
 * 承認者でない人・申請者本人への注意表示と確認モーダル (AC-APT-019a) は、 スポット入札画面の
 * 識別 (`SpotBidding/hooks/useApproverIdentity`、 AC-BSP-098) を使って本画面にも持つ。
 * 入札締切 (AC-BSP-099) は実行前に受渡日で判定する。 入札制限 (AC-BSP-097) は事前には再検証せず、
 * backend が入札制限違反で拒否した後に、 入札画面と同じ違反サマリを組み直して出す
 * (スポット・ブロックは受渡日の画面データと入札制限から既存の builder で、 時間前入札は
 * 注文の一覧から。 AC-APT-022)。 本画面を動作保証する段階で両画面の
 * 並存可否を改めて検討する (AC-BSP-094 の但し書き)。
 *
 * Phase 1 簡略化事項 (呼び出し元への明記):
 * 1. 「権限」フィルタ (自分が承認者/申請者) は未実装。
 *    BFF は全件返し、frontend 側でステータス / 業務プロセスのみ絞り込む。
 *    Phase 2 で UserDirectory と連携して実装予定。
 * 2. 「エリア」列は ApprovalTask proto に area フィールドが無いため省略 (Phase 1)。
 *    Phase 2 以降で BFF 集約に area を追加する予定。
 * 3. 「承認段階」列は単一段階 (段階 1/1) で固定表示 (Phase 1)。
 *    backend が単一段階のみサポートするため。多段階は Phase 2 で検討。
 */
import { useTranslation } from 'react-i18next'

import { PageBody } from '~/components/common/PageBody'
import { PageIntro } from '~/components/common/PageIntro'
import { PageLayout } from '~/components/common/PageLayout'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { ApprovalTaskList } from '~/pages/AdminApprovalTasks/components/ApprovalTaskList'
import { routePaths } from '~/routes/routePaths'

export const AdminApprovalTasksPage = (): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <PageLayout
      breadcrumbItems={[
        {
          label: t('page:adminApprovalTasks.breadcrumb.admin'),
          to: routePaths.adminHub.path,
        },
        {
          label: t('common:route.adminApprovalTasks'),
          bold: true,
        },
      ]}
    >
      <PageBody>
        {/* ページ説明 (design `.page-description`) */}
        <PageIntro description={t('page:adminApprovalTasks.description')} />

        {/* ApprovalTaskList は spot 入札の確認ステップからも埋め込み再利用される共有
            component (= SpotBidding/NormalBidTab)。 その埋め込み先は「page scroll に
            縦スクロールを委譲し、 二重スクロールを避ける」設計のため、 component 内部を
            PageScrollArea 化せず、 本ページ側だけで外側にスクロール領域を与える。 */}
        <PageScrollArea>
          <ApprovalTaskList />
        </PageScrollArea>
      </PageBody>
    </PageLayout>
  )
}
